import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    isDevMode,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import {
    MatLegacyTable as MatTable,
    MatLegacyTableDataSource as MatTableDataSource,
} from '@angular/material/legacy-table';
import { Router } from '@angular/router';
import { IAppRequest } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { TokenStorage } from '@app/core/token.storage';
import { FormFilterComponent } from '@app/modules/form-filter/form-filter.component';
import { TableColumnActionComponent } from '@app/modules/table/children/table-column-action/components/table-column-action.component';
import {
    EActionColumn,
    TActionColumn,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.enum';
import {
    IFilterRun,
    IGroupRun,
    IHideRun,
    IPinOrUnpinRun,
    IRenameDataSave,
    ISortRun,
    IValueForm,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.interface';
import {
    TCustomSortDirection,
    TGroupCustomAction,
} from '@app/modules/table/children/table-column-action/interfaces/table-column-action.type';
import { TableColumnActionService } from '@app/modules/table/children/table-column-action/table-column-action.service';
import {
    IDataColumnMenu,
    IPaletteColumnData,
} from '@app/modules/table/children/table-column-header-menu/table-column-header-menu.interface';
import { TableHeaderComponent } from '@app/modules/table/children/table-header/components/table-header.component';
import { TABLE_FIXED_COLUMS } from '@app/modules/table/constants/table.constants';
import { FormFilterSavedSearchesService } from '@app/modules/table/services/form-filter-saved-searches.service';
import { FormFilterTableControlService } from '@app/modules/table/services/form-filter-table-control.service';
import { TableEventService } from '@app/modules/table/services/table-event.service';
import { TableReorderButtonsService } from '@app/modules/table/services/table-reorder-buttons.service';
import { TableService } from '@app/modules/table/table.service';
import { IOutputEventData } from '@components/buttons-panel/buttons-panel.interface';
import { AppDialogService } from '@components/dialog/services/app-dialog.service';
import { stripHtml } from '@fui/fui-helpers';
import { IPaletteRecord } from '@modules/palettes/interfaces/palettes.interface';
import { FormaterPipe } from '@pipes/formater/formater.pipe';
import { FuiDestroyService } from '@services/destroy.service';
import { NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, Observable, of, zip } from 'rxjs';
import { catchError, debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { getSpinnerHtml } from 'src/shared/helpers/spinner-helpers';
import { FactorRowHistoryService } from '../factor-row/services/factor-row-history.service';
import { FactorRowService } from '../factor-row/services/factor-row.service';
import { FormFilterService } from '../form-filter/form-filter.service';
import { TableCellsService } from './children/table-cells/table-cells.service';
import { FilterDialogsComponent } from './dialogs/filter-dialogs/filter-dialogs.component';
import { TreeNode } from './dialogs/settings-table/interfaces/settings.table.dialog.interface';
import { SettingsTableDialogComponent } from './dialogs/settings-table/settings.table.dialog.component';
import { TableLayerDialogComponent } from './dialogs/table-layer-dialog/table-layer-dialog.component';
import { DEFAULT_PAGE_SIZE } from './pagination/custom.pagination.component';
import { TableSkeletonService } from './table-skeleton/table-skeleton.service';
import { IButton, InterfaceSettingsTable, InterfaceSettingsUserColumns, ITableCustomFilter } from './table.interface';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./styles/table.component.scss', './styles/table.component.adaptive.scss'],
    providers: [
        TableSkeletonService,
        FuiDestroyService,
        FormFilterTableControlService,
        FormFilterSavedSearchesService,
        FormaterPipe,
        TableEventService,
        TableReorderButtonsService,
        TableCellsService,
        FuiDestroyService,
        TableColumnActionService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    private readonly formBuilder = inject(FormBuilder);
    /** Признак полноэкранного режима */
    isFullScreenMode = false;
    /** Элемент пагинации */
    @ViewChild('UsersMatPaginatorTop', { static: true }) paginatorTables;
    /** Элемент сортировки */
    @ViewChild('UsersMatSort', { static: true }) sortTables;
    @ViewChild('dataMatTable', { static: false }) dataMatTable: MatTable<any>;
    @ViewChild('globalSearchInput', { static: true })
    globalSearchInput: FormControl = new FormControl();
    @ViewChild('tableContainer', { static: false }) tableContainer: ElementRef;
    @ViewChild('tableCard', { static: false, read: ElementRef }) tableCard;
    /** Заголовок таблицы */
    @ViewChild('tableHeader', { static: false, read: ElementRef }) tableHeader: ElementRef;
    @ViewChild('tableHeader', { static: false, read: TableHeaderComponent }) tableHeaderComponent: TableHeaderComponent;
    @ViewChild('matTable', { static: false }) matTable: ElementRef;
    /** Элемент Фильтра */
    @ViewChild(FormFilterComponent, { static: false })
    appFormFilter: FormFilterComponent;
    @ViewChild('filterExpansionPanel', { static: false })
    filterExpansionPanel: MatExpansionPanel;
    /** Элемент панели таблицы (кнопки, пагинация) */
    @ViewChild('tablePanel')
    tablePanel: ElementRef;

    /** Кнопки действий над столбцом */
    @ViewChild('groupButton', { read: ElementRef }) groupButtonRef!: ElementRef<HTMLElement>;
    @ViewChild('summarizeButton', { read: ElementRef }) summarizeButtonRef!: ElementRef<HTMLElement>;
    @ViewChild('sortButton', { read: ElementRef }) sortButtonRef!: ElementRef<HTMLElement>;
    @ViewChild('filterButton', { read: ElementRef }) filterButtonRef!: ElementRef<HTMLElement>;

    /** Компонент действий над столбцом (Popup) */
    @ViewChild('tableColumnAction', { static: false, read: TableColumnActionComponent })
    tableColumnAction!: TableColumnActionComponent;

    /**  Добавочные фильтры запроса */
    @Input() customFilter: ITableCustomFilter = {};
    /** Динамическая часть фильтра */
    @Input() dynamicFilter: any = null;
    /** Имя полей */
    @Input() dynamicFieldName: any = null;
    /** Добавление расширенного фильтра  */
    @Input() extendFilter: any = {};
    /** Прятать кнопки действий в расширенном фильтре, если нужно */
    @Input() hideExtendFilterButtons: { menu?: { setupFilter?: boolean; save?: boolean; map?: boolean } } | null = null;
    /** Локальный фильтр таблицы */
    @Input() isLocalFilter = false;

    /** Таблица оперирует с локальными данными. */
    @Input() hasLocalData = false;

    /** Измененный список колонок */
    @Input() editColumns = [];
    /**  Сервис взаимодействия страницы */
    @Input() tableService: TableService | any;

    /** Набор кнопок */
    @Input() buttons: {
        global?: IButton[];
        globalMenu?: IButton[];
        local?: IButton[];
    } = { global: [], globalMenu: [], local: [] };

    /** Добавление кнопок от настроек таблицы */
    @Input() addButtonsInTable: {
        [key: string]: {
            button: IButton;
            menu: string[];
        };
    } = {};

    @Input() showElement: boolean = false;
    //TODO: все output которые обрабатывают кнопки нужно заменить на buttonClick
    @Output() reassign = new EventEmitter<any>();
    @Output() saveCard = new EventEmitter<any>();
    @Output() add = new EventEmitter<any>();
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() copy = new EventEmitter<any>();
    @Output() edit = new EventEmitter<any>();
    @Output() customDeleteArray = new EventEmitter<any>();
    @Output() send = new EventEmitter<any>();
    @Output() report = new EventEmitter<any>();
    @Output() createPrivateOffice = new EventEmitter<any>();
    @Output() changeSubject = new EventEmitter<any>();
    @Output() deleteForPeriod = new EventEmitter<any>();
    @Output() activated = new EventEmitter<any>();
    @Output() deactivated = new EventEmitter<any>();
    @Output() openDialog = new EventEmitter<any>();
    @Output() dblClickRow = new EventEmitter<any>();
    // eslint-disable-next-line @angular-eslint/no-output-native
    @Output() show = new EventEmitter<any>();
    @Output() export = new EventEmitter<any>();
    @Output() import = new EventEmitter<void>();
    @Output() calculatePricing = new EventEmitter<any>();
    @Output() createGeocodeGeometry = new EventEmitter<any>();
    @Output() makeCalculation = new EventEmitter<any>();
    @Output() onFieldChange = new EventEmitter<any>();
    @Output() searchDuplicates = new EventEmitter<any>();
    @Output() generateReport = new EventEmitter<any>();
    @Output() copyAndCreate = new EventEmitter<any>();
    @Output() acceptTheReport = new EventEmitter<any>();
    @Output() rejectTheReport = new EventEmitter<any>();
    @Output() viewSection = new EventEmitter<any>();
    @Output() readArray = new EventEmitter<any>();
    @Output() exportPdf = new EventEmitter<any>();
    @Output() showReport = new EventEmitter<any>();
    @Output() addOnService = new EventEmitter<void>();
    @Output() showFile = new EventEmitter<any>();
    @Output() moveMessage = new EventEmitter<any>();
    @Output() restoreMessage = new EventEmitter<any>();
    @Output() reply = new EventEmitter<any>();
    @Output() setMapLink = new EventEmitter<any>();
    @Output() linkMap = new EventEmitter<any>();
    @Output() getChangeTracking = new EventEmitter<any>();
    @Output() generateCharges = new EventEmitter<any>();
    @Output() exportChargePaymentReport = new EventEmitter<any>();
    @Output() deleteGeometry = new EventEmitter<any>();
    /* Открыть окно настроек фильтра для регламентированного очета */
    @Output() getReportFilterSettings = new EventEmitter<any>();
    @Output() addLayer = new EventEmitter<any>();
    /** Открытие диалогового окна настройки команды ОКС */
    @Output() editTeamList = new EventEmitter<any>();
    /** Переопределяем удаление в таблице */
    @Output() customDelete = new EventEmitter<any>();
    /** Отправка данных по простым нажатиям кнопки */
    @Output() buttonClick = new EventEmitter<IOutputEventData<string>>();

    /**
     * Событие, выбрасываемое при старте выполнения метода getData
     */
    @Output() dataRequested = new EventEmitter<void>();

    /**
     * Событие, выбрасываемое по завершению выполнения метода getData
     * Возвращает новые данные, полученные таблицей
     */
    @Output() dataReceived = new EventEmitter<any[]>();

    /** Настройки дерева */
    @Input() groupByTreeColumns: {
        columnParent: string;
        columnGroup: string;
        isTree: boolean;
        rootId?: string | number;
    } = {
        columnParent: 'parent_id',
        columnGroup: null,
        isTree: false,
        rootId: 1,
    };
    /** Настройка редактирования строк */
    @Input() settingsEditFields: {
        isEdit: boolean;
        excludeField: string;
        fields: { [key: string]: any };
    } = {
        isEdit: false,
        excludeField: null,
        fields: {},
    };

    /** Полные данные записей */
    filteredDataTreeAll: any[] = [];

    classTable: string = 'table-container-random-' + (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    loadSettings: boolean = false;
    /** Выбранный фильтр */
    selectFilter: any = null;
    /** Флаг первой загрузки фильтра */
    isFirstLoadFilter = true;
    /** Данные цветов по статусам */
    dataPalette: IPaletteRecord[] = null;
    /**
     * Форма поиска
     */
    form: FormGroup = this.formBuilder?.group({});
    formEdit: FormGroup = this.formBuilder?.group({});
    /**
     * Ошибки на странице
     * @type {string}
     */
    requestError = '';
    /**
     * Элемент таблицы
     * @type {MatTableDataSource<>}
     */
    dataTables = new MatTableDataSource();

    /** Флаг группировки таблицы (нужен из-за специфичной группировки в субьектах автоматизации) */
    isGroup: boolean = false;

    /** Полноэкранный спиннер */
    isSpinnerFullScreen = false;

    /** Имя для спиннера компонента */
    spinnerName = '';

    selection = new SelectionModel(true, []);
    /**
     * Число записей таблицы
     * @type {number}
     */
    lengthTables = 0;
    /** Ширина колонки по умолчанию */
    defaultWidth: number = 80;
    /** Минимальная ширина колонки */
    minColumnWidth: number = 45;
    /**
     * Набор количества строк для таблицы
     * @type {number[]}
     */
    pageSizeOptionsTables = [20, 50, 100, 200];
    /**  Событие выбора количества записей на странице */
    pageEvent: PageEvent;
    /** Выбор палитры по которой будут краситься строки */
    mainPalette: string = 'status_date';
    readonly #cdr = inject(ChangeDetectorRef);
    readonly formFilter = inject(FormFilterTableControlService);
    readonly tableReorderButtonsService = inject(TableReorderButtonsService);
    public readonly formFilterSavedSearchesService = inject(FormFilterSavedSearchesService);
    showSavedFiltersButton$ = this.formFilterSavedSearchesService.filterRecords$.pipe(
        map((filters) => filters.filter((x) => Number(x.id) !== -1).length > 0),
    );
    /** Сервис действий над столбцами таблицы */
    readonly #tableColumnActionService = inject(TableColumnActionService);
    readonly tableEventService = inject(TableEventService);
    private readonly tableCellsService = inject(TableCellsService);
    private readonly destroy$ = inject(FuiDestroyService);
    /** Метод для очистки тегов */
    protected readonly stripHtml = stripHtml;
    /** Массив кнопок таблицы */
    globalButtons$: BehaviorSubject<IButton[]> = new BehaviorSubject<IButton[]>([]);
    /** Массив кнопок глобального меню таблицы */
    globalMenuButtons$: BehaviorSubject<IButton[]> = new BehaviorSubject<IButton[]>([]);
    /** Событие нажатие клавиши shift */
    shiftKeyPressed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    /**
     * Флаг пустого результата поиска
     */
    emptyResult$ = new BehaviorSubject<boolean>(true);
    /** Показывать/скрывать основной контент таблицы */
    showTableContent$ = new BehaviorSubject<boolean>(true);
    /** Фильтры таблицы */
    filterTable = {};
    /** Набор фильтров */
    extendFilterRecordsModel = [];
    /** Флаг отображения фильтров */
    isExtendFilterShowModel = false;
    /** ИД фильтра */
    extendFilterIdModel = '';
    /** Настройки таблицы */
    settingsTable: InterfaceSettingsTable = {
        displayedColumns: ['select'],
    };

    /**
     * Принудительное отображение панели кнопок в случае наличия расширенного поиска или сохранённых поисков.
     */
    forceButtonPanel$ = new BehaviorSubject<boolean>(false);

    /**
     * Обычно показ панели кнопок регулируется наличием расширенного поиска или сохранённых поисков.
     * Но если надо, можно явно задать здесь.
     */
    #showButtonPanel$ = new BehaviorSubject<boolean>(true);

    /**
     * Столбцы индикаторов (палитры), по которым можно выполнять действия
     */
    showMenuColumnPalette$: BehaviorSubject<Record<string, IPaletteColumnData>> = new BehaviorSubject<
        Record<string, IPaletteColumnData>
    >({});

    /** Название действия над колонкой (компонент TableColumnActionComponent) */
    columnActionType: TActionColumn;

    /** Задание строки подсказки в заголовке компонента TableColumnActionComponent */
    columnActionTooltip = new BehaviorSubject<null | { preloader: boolean; text: string }>(null);

    /** Значения действия над столбцом */
    valuesFormColumnActions: IValueForm[] = [];

    @Input() set showButtonPanel(value: boolean) {
        this.#showButtonPanel$.next(value);
    }

    /**
     * Текущая таблица
     */
    @Input() item;
    /**
     * Текущий язык
     */
    @Input() currentLangRelationForm;
    /**
     * Показать все изменения
     */
    @Input() isChangeTracking = false;
    /**
     * Наименование модуля таблицы для сохранения пользовательских настроек
     */
    @Input() tableModule: string;
    /**  Показывать заголовок таблицы */
    @Input() showTableHeader: boolean = true;
    /**
     * Должна ли таблица занимать всю высоту экрана (как в реестрах, например)
     */
    readonly #isFullHeight$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    dynamicSystemFields: string[] = ['status_name'];
    /** Глобальный поиск */
    globalFilter = '';

    /** Флаг загрузки данных для отрисовки */
    public isLoaded$ = new BehaviorSubject<boolean>(false);

    /** Спиннер */
    readonly spinnerHtml = getSpinnerHtml();

    public readonly trackByUserColumnsName = (column) => column.title;

    public set isFullHeight(val) {
        this.#isFullHeight$.next(val);
    }

    public get isFullHeight$() {
        return this.#isFullHeight$.asObservable();
    }

    constructor(
        public router: Router,
        public notifier: NotifierService,
        public dialog: MatDialog,
        public spinner: NgxSpinnerService,
        public appService: AppService,
        public filterService: FormFilterService,
        private dialogConfirm: AppDialogService,
        private token: TokenStorage,
        public factorRowHistoryService: FactorRowHistoryService,
        public factorRowService: FactorRowService,
        public tableSkeleton: TableSkeletonService,
    ) {
        this.spinnerName = AppHelper.randomInteger(1, 999999).toString();
        this.tableCellsService.getSettingsTableFn = () => this.settingsTable;
    }

    ngOnChanges(changes: SimpleChanges) {
        this.isSpinnerFullScreen = this.formFilter.isFilter && this.selectFilter;
        if (this.editColumns.length > 0) {
            this.settingsTable.displayedColumns = this.editColumns;
        }
        for (const propName in changes) {
            if (changes.hasOwnProperty(propName)) {
                if (propName === 'tableService' && changes[propName].previousValue !== undefined) {
                    this.ngAfterViewInit();
                }
                if (propName === 'buttons') {
                    this.adjustButtons();
                }
            }
        }
    }

    /**
     * Вызов родительской функции
     * @param type тип
     * @param value значение
     * @param event событие нажатия
     */
    callParent(type, value, event) {
        this.appService.openNewTab = event.ctrlKey || event.metaKey;
        switch (type) {
            case 'add':
                this.add.next(value);
                break;
            case 'send':
                this.send.next(value);
                break;
            case 'report':
                this.report.next(value);
                break;
            case 'edit':
                this.saveScrollTable();
                this.edit.next(value);
                break;
            case 'deleteArray':
                this.removeRecords(value);
                break;
            case 'calculatePricing':
                this.calculatePricing.next(value);
                break;
            case 'createGeocodeGeometry':
                this.createGeocodeGeometry.next(value);
                break;
            case 'makeCalculation':
                this.makeCalculation.next(value);
                break;
            case 'changeSubject':
                this.changeSubject.next(value);
                break;
            case 'deleteForPeriod':
                this.deleteForPeriod.next(value);
                break;
            case 'activated':
                this.activated.next(value);
                break;
            case 'deactivated':
                this.deactivated.next(value);
                break;
            case 'customDeleteArray':
                this.customDeleteArray.next(value);
                break;
            case 'openDialog':
                this.openDialog.next(value);
                break;
            case 'searchDuplicates':
                this.searchDuplicates.next(value);
                break;
            case 'generateReport':
                const settings = {
                    rows: this.settingsTable.userSize,
                    page: this.settingsTable.userIndex,
                    sort: this.getSortParam(this.settingsTable.userSort),
                    global_filter: this.globalFilter,
                    custom_filter: this.customFilter,
                    extended_filter: this.extendFilter,
                    filter: this.settingsTable.userFilter,
                    settings: this.settingsTable,
                    value,
                };
                this.generateReport.next(settings);
                break;
            case 'dblClickRow':
                this.saveScrollTable();
                this.dblClickRow.next(value);
                break;
            case 'show':
                this.saveScrollTable();
                this.show.next(value);
                break;
            case 'createPrivateOffice':
                this.createPrivateOffice.next(value);
                break;
            case 'export':
                const data = {
                    rows: this.settingsTable.userSize,
                    page: this.settingsTable.userIndex,
                    sort: this.getSortParam(this.settingsTable.userSort),
                    global_filter: this.globalFilter,
                    custom_filter: this.customFilter,
                    extended_filter: this.extendFilter,
                    filter: this.settingsTable.userFilter,
                    settings: this.settingsTable,
                    checked_rows: value.map(row => row.id)
                };
                this.export.next(data);
                break;
            case 'import':
                this.import.next();
                break;
            case 'addOnService':
                this.addOnService.next();
                break;
            case 'showFile':
                this.showFile.next(value);
                break;
            case 'copyAndCreate':
                this.copyAndCreate.next(value);
                break;
            case 'acceptTheReport':
                this.acceptTheReport.next(value);
                break;
            case 'rejectTheReport':
                this.rejectTheReport.next(value);
                break;
            case 'viewSection':
                this.viewSection.next(value);
                break;
            case 'readArray':
                this.readArray.next(value);
                break;
            case 'exportPdf':
                this.exportPdf.next(value);
                break;
            case 'showReport':
                this.showReport.next(value);
                break;
            case 'moveMessage':
                this.moveMessage.next(value);
                this.getData(false);
                break;
            case 'restoreMessage':
                this.restoreMessage.next(value);
                this.getData(false);
                break;
            case 'reply':
                this.reply.next(value);
                break;
            case 'setMapLink':
                this.setMapLink.next(value);
                break;
            case 'linkMap':
                this.linkMap.next(value);
                break;
            case 'addLayer':
                this.addLayer.next(value);
                break;
            case 'getChangeTracking':
                this.getChangeTracking.next(value);
                break;
            case 'generateCharges':
                this.generateCharges.next(value);
                break;
            case 'exportChargePaymentReport':
                this.exportChargePaymentReport.next(value);
                break;
            case 'deleteGeometry':
                this.deleteGeometry.next(value);
                break;
            case 'getReportFilterSettings':
                this.getReportFilterSettings.next(value);
                break;
            case 'reassign':
                this.reassign.next(value);
                break;
            case 'editTeamList':
                this.editTeamList.next(value);
                break;
            case 'customDelete':
                this.customDelete.next(value);
                break;
            case 'copy':
                this.copy.next(value);
                break;
            case 'fullScreenMode':
                if (this.isFullScreenMode) {
                    this.#turnFullScreenOff();
                } else {
                    // Полноэкранные режим
                    this.isFullScreenMode = true;
                    this.appService.showSideMenu.next(false);
                    this.notifier.notify('success', this.appService.language_dictionary.full_screen_mode_on);
                    this.adjustButtons();
                }
                this.broadcastButtonChanges();
                break;
            default:
                this.buttonClick.next({ name: type, data: value });
        }
    }

    /**
     * Сохранение скролла таблицы
     */
    saveScrollTable(clear: boolean = false) {
        let scroll = 0;
        if (!clear && document.querySelector('.mat-table-container')) {
            scroll = document.querySelector('.mat-table-container').scrollTop;
        }
        this.token.saveScrollTable(this.getTableKey('ScrollTable'), scroll);
    }

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        if (this.tableService) {
            this.tableSkeleton.show();
        }
        if (this.isLocalFilter) {
            this.extendFilterRecordsModel = [];
            this.isExtendFilterShowModel = false;
            this.extendFilterIdModel = '';
        } else {
            // Удаляем доступные фильтры из заголовка приложения
            this.formFilter.clear();

            this.formFilter.savedSerchesService.selectedFilterId$
                .pipe(
                    filter((id) => !!id),
                    takeUntil(this.destroy$),
                )
                .subscribe((id) => {
                    this.setActiveFilter(id);
                });
        }

        // Принудительное отображение панели кнопок в случае наличия расширенного поиска или сохранённых поисков.
        combineLatest([
            this.#showButtonPanel$,
            this.formFilter.isFilter$,
            this.formFilterSavedSearchesService.filterRecordsLength$,
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([showButtonPanel, isFilter, filterRecordsLength]) => {
                this.forceButtonPanel$.next(showButtonPanel && (isFilter || filterRecordsLength > 0));
            });

        // Подписываемся на изменение имени колонки
        this.#tableColumnActionService.eventNewColumnName$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data: IRenameDataSave) => {
                this.#renameColumn(data);
            });
    }

    /** Изменение названия колонки таблицы в настройках пользователя */
    #renameColumn(data: IRenameDataSave) {
        // Сохраняем измененное название
        this.settingsTable.userColumns.map((item) => {
            if (item.name === data.name) {
                item.title = data.title;
            }
        });
        this.settingsTable.userTree.select.map((item) => {
            if (item.alias === data.name) {
                item.name = data.title;
            }
        });
        // Сохраняем изменения в бд
        this.#afterChangingSettings(false);
    }

    /**
     * Разрушение компонента
     */
    ngOnDestroy() {
        if (this.isLocalFilter) {
            this.extendFilterRecordsModel = [];
            this.isExtendFilterShowModel = false;
            this.extendFilterIdModel = '';
        } else {
            this.formFilter.clear();
        }
        this.buttonClick.complete();
        this.appService.showSideMenu.next(true);
    }

    /**
     * Событие после завершения инициализации вьюшки
     */
    ngAfterViewInit(): void {
        this.broadcastButtonChanges();
        let localFilter = null;
        this.loadSettings = false;
        let settingData = Object.assign({}, this.customFilter);
        if (this.dynamicFilter) {
            settingData = Object.assign(settingData, this.dynamicFilter);
        }
        if (this.tableModule) {
            settingData.module = this.tableModule;
        }
        this.spinner.show(this.spinnerName);

        const request: Observable<{ type: string; result: IAppRequest }>[] = [
            this.tableService.getTableSettings(settingData).pipe(
                map((result: IAppRequest) => ({
                    type: 'settings',
                    result,
                })),
            ),
        ];

        if (this.tableService.getFiltersList) {
            request.push(
                this.tableService.getFiltersList(this?.dynamicFilter ?? {}).pipe(
                    catchError(() => of(null)),
                    map((result: IAppRequest) => ({
                        type: 'filter',
                        result,
                    })),
                ),
            );
            localFilter = this.token.getFilterTable(this.getTableKey('FilterTable'));
        } else {
            // Удаляем доступные фильтры из заголовка приложения
            this.formFilter.savedSerchesService.filterRecords = [];
            this.formFilter.isFilter = false;
        }

        zip(request)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (result: { type: string; result: IAppRequest }[]) => {
                    if (!result) {
                        throw Error('Invalid server response');
                    }

                    /** Данные фильтров */
                    const filterData = result.find((row) => row.type === 'filter')?.result;
                    /** Данные настроек таблицы */
                    const settingsData = result.find((row) => row.type === 'settings')?.result;

                    let isFilter = false;

                    if (filterData && filterData.is_error === false && filterData?.data.length) {
                        isFilter = true;
                        if (this.isLocalFilter) {
                            this.extendFilterRecordsModel = filterData.data;
                            this.formFilter.isFilter = Boolean(filterData.data?.length);
                        } else if (Array.isArray(filterData.data)) {
                            this.formFilter.savedSerchesService.filterRecords = filterData.data;
                            this.formFilter.isFilter = Boolean(
                                this.formFilter.savedSerchesService.filterRecords?.length,
                            );
                        }
                    }

                    if (!settingsData || settingsData.is_error === true) {
                        throw new Error(settingsData.message);
                    }

                    this.settingsTable = Object.assign(this.settingsTable, settingsData.data);

                    // Если пришли настройки группировки, значит отображаем таблицу в древовидном виде
                    if (this.settingsTable.userGroup && this.settingsTable.userGroup.length > 0) {
                        this.groupByTreeColumns.isTree = true;
                        this.isGroup = true;
                    }

                    /* Добавление кнопок в меню таблицы (во все 3) */
                    this.adjustButtons();

                    if (!this.settingsTable.displayedColumns) {
                        this.settingsTable.displayedColumns = [];
                    }

                    if (!this.settingsTable.userColumns) {
                        this.setFirstConfig();
                    } else {
                        this.setAllColumns();
                    }
                    this.normalizeUserSort();
                    this.clearDisplayedColumns();

                    /* Установка данных для палитры */
                    this.setPaletteData(settingsData.data.palette);
                    this.settingsTable.displayedColumns = this.#sortDisplayedColumns(
                        this.settingsTable.displayedColumns,
                    );

                    this.setCellForColumns();
                    this.dataTables.paginator = this.paginatorTables;
                    this.dataTables.sort = this.sortTables;
                    if (this.isLocalFilter) {
                        if (this.settingsTable && this.settingsTable.filterId) {
                            this.extendFilterIdModel = this.settingsTable.filterId;
                            this.setActiveFilter(this.extendFilterIdModel);
                        } else if (filterData && filterData?.data && filterData.data.length) {
                            this.extendFilterIdModel = filterData.data[0].id;
                            this.setActiveFilter(this.extendFilterIdModel);
                        }
                        if (
                            this.extendFilterRecordsModel &&
                            this.extendFilterRecordsModel.length &&
                            !this.selectFilter
                        ) {
                            this.extendFilterRecordsModel.forEach((record) => {
                                if (record.id == this.extendFilterIdModel) {
                                    this.selectFilter = Object.assign({}, record);
                                }
                            });
                        }
                    } else {
                        if (this.formFilter.savedSerchesService.filterRecordsLength) {
                            let isFilterId = false;
                            this.formFilter.savedSerchesService.filterRecords$
                                .pipe(take(1), takeUntil(this.destroy$))
                                .subscribe((list) => {
                                    list.forEach((value) => {
                                        if (value.id == this.settingsTable.filterId) {
                                            isFilterId = true;
                                        }
                                    });
                                    if (!isFilterId) {
                                        this.settingsTable.filterId = null;
                                    }
                                });
                        } else {
                            this.settingsTable.filterId = null;
                        }
                        if (this.settingsTable && isFilter && this.settingsTable.filterId) {
                            this.formFilter.savedSerchesService.selectedFilterId = this.settingsTable.filterId;
                        } else if (filterData && filterData.data && filterData.data.length) {
                            this.formFilter.savedSerchesService.selectedFilterId = filterData.data[0].id;
                        }
                        if (this.formFilter.savedSerchesService.filterRecords.length && !this.selectFilter) {
                            const index = this.formFilter.savedSerchesService.filterRecords.findIndex(
                                (record) => record.id == this.formFilter.savedSerchesService.selectedFilterId,
                            );
                            if (index > -1) {
                                this.selectFilter = Object.assign(
                                    {},
                                    this.formFilter.savedSerchesService.filterRecords[index],
                                );
                            }
                        }
                    }
                    if (
                        ((!this.settingsTable.filterId || Number(this.settingsTable.filterId) === -1) &&
                            !localFilter &&
                            !(this.selectFilter && this.selectFilter.query)) ||
                        !this.tableService.getFiltersList
                    ) {
                        this.getData(this.tableService.isSearch);
                    }
                    this.broadcastButtonChanges();
                    this.loadSettings = true;
                },
                error: (error: unknown) => {
                    console.error(error);

                    this.spinner.hide(this.spinnerName);
                    this.isLoaded$.next(true);
                    this.showTableHeader = false;
                    this.requestError = this.appService.language_dictionary.system_no_connection_with_system;
                    this.tableSkeleton.hide();
                    this.#cdr.detectChanges();
                },
            });

        // Подписка на изменение выделения строк
        this.selection.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.buttons = this.tableReorderButtonsService.reorderButtons(this.buttons, this.selection.selected.length);
            this.broadcastButtonChanges();
        });
        this.selectionKeyboardHandler();
    }

    /**
     * При инициализации и каждый раз, когда список кнопок задаётся извне,
     * нам нужно дополнить его дополнительными кнопками из addButtonsInTable
     * согласно настройкам доступности кнопок из settingsTable
     */
    private adjustButtons() {
        const buttonList = Object.keys(this.addButtonsInTable);
        if (buttonList?.length) {
            buttonList.forEach((keyButton) => {
                if (this.settingsTable?.buttons && this.settingsTable?.buttons[keyButton]) {
                    const buttonToAdd = this.addButtonsInTable[keyButton];
                    if (buttonToAdd.button.click === 'fullScreenMode') {
                        if (this.isFullScreenMode) {
                            buttonToAdd.button.color = 'soft';
                        } else {
                            buttonToAdd.button.color = 'flat';
                        }
                    }
                    buttonToAdd.menu.forEach((menuButton) => {
                        if (this.buttons[menuButton].findIndex((x) => x.name === buttonToAdd.button.name) === -1) {
                            this.buttons[menuButton].push(buttonToAdd.button);
                        }
                    });
                }
            });
        }

        [this.buttons.globalMenu].forEach((arr) => {
            arr.sort((a, b) => {
                const isDelA = a?.icon.indexOf('delete');
                const isDelB = b?.icon.indexOf('delete');
                return isDelA >= isDelB ? 1 : -1;
            });
        });

        this.broadcastButtonChanges();
    }

    /**
     * Оповестить слушателей об изменении текущего набора кнопок
     */
    private broadcastButtonChanges() {
        this.globalButtons$.next(this.buttons?.global ?? []);
        this.globalMenuButtons$.next(this.buttons?.globalMenu ?? []);
    }

    /**
     * Установка данных для палитры
     */
    setPaletteData(data: any) {
        const paletteData: IPaletteRecord[] = typeof data === 'object' ? Object.values(data) : [];
        if (!paletteData.length) {
            return;
        }

        this.dataPalette = [];
        paletteData.forEach((palette) => {
            const hasData = !palette.merged && palette.data && palette.data.length;
            const hasMergedData = palette.merged && palette.merged_data && palette.merged_data.length;
            if (hasData || hasMergedData) {
                this.dataPalette.push(palette);
                const paletteColumn = 'system_color_' + palette.index;

                // Проверяем, существует ли уже такой столбец
                if (this.settingsTable.displayedColumns.includes(paletteColumn)) {
                    // Если столбец уже существует, пропускаем его добавление
                    return;
                }

                const paletteIndex = this.dataPalette.indexOf(palette);
                if (this.settingsTable.displayedColumns[paletteIndex] !== paletteColumn) {
                    this.settingsTable.displayedColumns.splice(paletteIndex + 1, 0, paletteColumn);
                }
            }
        });
        this.#checkDisplayPaletteIndicators();
        this.#setStatePaletteColumns(paletteData);

        /* Установка палитры, по которой будет происходить покраска */
        this.getMainPalette(this.dataPalette);
    }

    /**
     * Получение ключа для хранения данных
     * @param stringKey определяющая часть ключа
     */
    getTableKey(stringKey: string) {
        let key = this.tableService?.className + stringKey;
        if (this.dynamicFilter && this.dynamicFilter.factor_id) {
            key += this.dynamicFilter.factor_id;
        }
        if (this.dynamicFilter && this.dynamicFilter.relation_id) {
            key += this.dynamicFilter.relation_id;
        }
        return key;
    }

    /**
     * Проставление первичных данных
     */
    setFirstConfig() {
        const adminColumns = {};
        const idx = (col) => (this.dynamicFilter ? col.id + '_' + (col.relation_id ? col.relation_id : '') : col.name);
        if (this.settingsTable.adminColumns && this.settingsTable.adminColumns.length) {
            this.settingsTable.adminColumns.forEach((column) => {
                adminColumns[idx(column)] = column;
            });
        }
        this.settingsTable.userIndex = 1;
        this.settingsTable.userSize = DEFAULT_PAGE_SIZE;
        this.settingsTable.userColumns = [];
        this.settingsTable.userTree = {
            select: [],
            all: [],
        };
        this.settingsTable.allColumns.forEach((allColumn) => {
            const node = { name: allColumn.entity, level: 1, children: [] };
            allColumn.columns.forEach((column, index) => {
                node.children.push({
                    parent: allColumn.entity,
                    name: column.title,
                    alias: column.name,
                    type: column.type,
                    factor_type: column.factor_type,
                    is_sticky: false,
                    is_main: false,
                    is_fixed: column.is_fixed,
                    width: column.width,
                    columns: [],
                    level: 2,
                    transformParam: column?.transformParam,
                });
                if (this.dynamicFilter) {
                    node.children[node.children.length - 1].id = column.id;
                    if (column.relation_id) {
                        node.children[node.children.length - 1].relation_id = column.relation_id;
                    }
                }
                if (this.settingsTable.adminColumns && this.settingsTable.adminColumns.length) {
                    if (adminColumns[idx(column)]) {
                        node.children.splice(node.children.length - 1, 1);
                        adminColumns[idx(column)].entity = allColumn.entity;
                        adminColumns[idx(column)].is_main = allColumn.is_main && index == 0;
                    }
                } else if (allColumn.is_main && index < 5) {
                    node.children.splice(node.children.length - 1, 1);
                    this.addDefaultColumn(allColumn.entity, column, index == 0);
                }
            });
            this.settingsTable.userTree.all.push(Object.assign({}, node));
        });
        if (this.settingsTable.adminColumns && this.settingsTable.adminColumns.length) {
            this.settingsTable.adminColumns.forEach((column) => {
                this.addAdminColumn(adminColumns[idx(column)].entity, column);
            });
        }
        this.settingsTable.userSort = (this.settingsTable.adminSort ?? []).map((x) => ({ name: x.name, sort: x.sort }));
    }

    /**
     * Добавление колонки из админских настроек
     * @param entity сущность
     * @param column колонка
     * @param is_main колонка главной сущности
     */
    addAdminColumn(entity: string, column: InterfaceSettingsUserColumns) {
        this.settingsTable.userColumns = [...this.settingsTable.userColumns, column];
        this.settingsTable.userTree.select.push({
            parent: entity,
            id: column.id as string,
            name: column.title,
            alias: column.name,
            type: column.type,
            factor_type: column.factor_type,
            is_sticky: column.is_sticky,
            is_main: false,
            width: column.width,
            columns: [],
            level: 1,
            transformParam: column?.transformParam,
        });
        if (column.is_fixed) {
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].is_fixed =
                column.is_fixed;
        }
        if (typeof column.relation_id != 'undefined') {
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].relation_id =
                column.relation_id;
        }
        this.settingsTable.displayedColumns = [...this.settingsTable.displayedColumns, column.name];
    }

    /**
     * Добавление колонки по умолчанию
     * @param entity сущность
     * @param column колонка
     * @param is_main колонка главной сущности
     */
    addDefaultColumn(entity: string, column: InterfaceSettingsUserColumns, is_main: boolean) {
        this.settingsTable.userColumns.push({
            name: column.name,
            title: column.title,
            type: column.type,
            factor_type: column.factor_type,
            is_sticky: false,
            is_main,
            is_group: false,
            width: !!column.width ? column.width : this.defaultWidth,
            is_fixed: !!column.is_fixed ? column.is_fixed : false,
            transformParam: column?.transformParam,
        });
        if (column.type === 'boolean') {
            this.filterTable[column.name] = '';
        }
        this.settingsTable.userTree.select.push({
            id: column.id as string,
            parent: entity,
            name: column.title,
            alias: column.name,
            type: column.type,
            factor_type: column.factor_type,
            is_sticky: false,
            is_main: false,
            is_fixed: false,
            width: this.defaultWidth,
            columns: [],
            level: 1,
            transformParam: column?.transformParam,
        });
        if (this.dynamicFilter) {
            this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].id = column.id;
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].id = column.id as string;
            this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].name = column.name;
            this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].alias = column.name;
            if (column.relation_id) {
                this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].relation_id =
                    column.relation_id;
                this.settingsTable.userTree.select[this.settingsTable.userTree.select.length - 1].relation_id =
                    column.relation_id;
            }
        }
        if (this.dynamicFilter) {
            this.settingsTable.displayedColumns.push(
                this.settingsTable.userColumns[this.settingsTable.userColumns.length - 1].name,
            );
        } else {
            this.settingsTable.displayedColumns.push(column.name);
        }
    }

    /**
     * Очистка колонок
     * Удаляет столбец buttons, если он приходит из БД.
     */
    clearDisplayedColumns() {
        const index = this.settingsTable.displayedColumns.indexOf('buttons');
        if (index !== -1) {
            this.settingsTable.displayedColumns.splice(index, 1);
        }
    }

    /**
     * Проставление всех колонок
     */
    setAllColumns() {
        const selected = {};
        this.settingsTable.userTree.select.forEach((node) => {
            if (this.dynamicFilter) {
                selected[node.id + '_' + (node.relation_id ? node.relation_id : '')] = true;
                if (node.columns && node.columns.length) {
                    node.columns.forEach((child) => {
                        selected[child.id + '_' + (child.relation_id ? child.relation_id : '')] = true;
                    });
                }
            } else {
                selected[node.alias] = true;
                if (node.columns && node.columns.length) {
                    node.columns.forEach((child) => {
                        selected[child.alias] = true;
                    });
                }
            }
        });
        this.settingsTable.userTree.all = [];
        this.settingsTable.allColumns.forEach((allColumn) => {
            const node = { name: allColumn.entity, level: 1, children: [] };
            allColumn.columns.forEach((column) => {
                if (this.dynamicFilter) {
                    if (!selected[column.id + '_' + (column.relation_id ? column.relation_id : '')]) {
                        node.children.push({
                            parent: allColumn.entity,
                            name: column.title,
                            alias: column.name,
                            type: column.type,
                            factor_type: column.factor_type,
                            is_sticky: false,
                            is_main: false,
                            width: this.defaultWidth,
                            is_fixed: false,
                            columns: [],
                            level: 2,
                            transformParam: column?.transformParam,
                        });
                        node.children[node.children.length - 1].id = column.id;
                        if (column.relation_id) {
                            node.children[node.children.length - 1].relation_id = column.relation_id;
                        }
                    }
                } else {
                    if (!selected[column.name]) {
                        node.children.push({
                            parent: allColumn.entity,
                            name: column.title,
                            alias: column.name,
                            type: column.type,
                            factor_type: column.factor_type,
                            is_sticky: false,
                            is_main: false,
                            width: this.defaultWidth,
                            is_fixed: false,
                            columns: [],
                            level: 2,
                            transformParam: column?.transformParam,
                        });
                    }
                }
            });
            this.settingsTable.userTree.all.push(Object.assign({}, node));
        });
    }

    /**
     * Получение данных таблицы
     * @param isPage флаг изменения страницы
     * TODO: было бы круто этот метод вызывать снаружи и подписываться на его завершение
     */
    getData(isPage: boolean): void {
        this.isSpinnerFullScreen = this.formFilter.isFilter && this.selectFilter;
        this.requestError = '';
        if (!isPage) {
            this.settingsTable.userIndex = 1;
        }

        const data = {
            rows: this.settingsTable.userSize,
            page: this.settingsTable.userIndex,
            sort: this.getSortParam(this.settingsTable.userSort),
            group: this.settingsTable.userGroup,
            global_filter: this.globalFilter,
            custom_filter: this.customFilter,
            extended_filter: this.extendFilter,
            filter: this.settingsTable.userFilter,
            module: this.tableModule,
        };
        this.spinner.show(this.spinnerName);
        this.tableSkeleton.show();
        this.dataRequested.emit();
        this.columnActionTooltip.next({ preloader: true, text: this.appService.language_dictionary.system_loading });

        this.tableService
            .getLists(data)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    if (!this.isLoaded$.value) {
                        this.isLoaded$.next(true);
                        setTimeout(() => {
                            this.setAutoWidth();
                            this.isSpinnerFullScreen = false;
                        }, 0);
                    }
                    this.spinner.hide(this.spinnerName);
                    this.tableSkeleton.hide();
                    this.dataReceived.emit(result?.data?.rows ?? []);
                    this.columnActionTooltip.next({
                        preloader: false,
                        text: `${this.appService.language_dictionary.search_found}: ${result?.data?.rows.length ?? 0}`,
                    });
                    setTimeout(() => {
                        this.increaseCount();
                    });

                    if (!result || result?.is_error === true) {
                        throw new Error(result?.message);
                    }

                    // текущий номер страницы не попадает в набор данных #55210
                    // запрашиваем заново первую страницу
                    if (isPage && Number(result.data?.records ?? 0) === 0 && data.page > 1) {
                        setTimeout(() => this.getData(false));
                        return;
                    }

                    this.viewDataTable(result.data);
                    setTimeout(() => {
                        this.setAutoWidth();
                    }, 0);
                    if (this.formFilter?.isFilter) {
                        this.formFilter.recordsFound = result.data?.records;
                    }
                    this.detectChanges();
                },
                error: (error: Error) => {
                    if (isDevMode()) {
                        console.error(error);
                    }

                    if (!this.isLoaded$.value) {
                        this.isLoaded$.next(true);
                        setTimeout(() => {
                            this.setAutoWidth();
                        }, 0);
                    }

                    this.spinner.hide(this.spinnerName);
                    this.tableSkeleton.hide();
                    this.isLoaded$.next(true);
                    this.dataReceived.emit([]);
                    this.isSpinnerFullScreen = false;

                    this.requestError = error?.message;
                    if (isDevMode()) {
                        console.error(this.requestError);
                    }
                    setTimeout(() => {
                        this.increaseCount();
                    });
                    this.detectChanges();
                },
            });
    }

    /**
     * Отображение данных таблицы
     * @param data данные запроса
     */
    viewDataTable(data: any) {
        this.dataTables.filteredData = data.rows;

        // Отображение древовидной структуры
        if (this.groupByTreeColumns.isTree) {
            this.groupByTree();
        }

        if (this.settingsEditFields?.isEdit) {
            this.initFormEdit();
        }
        this.emptyResult$.next(this.dataTables.filteredData.length <= 0);
        this.lengthTables = parseInt(data.records, 10);
        this.dataTables._updatePaginator(this.lengthTables);
        this.selection.clear();
        setTimeout(() => {
            if (this.dataMatTable) {
                this.dataMatTable.updateStickyColumnStyles();
            }

            const scroll = this.token.getScrollTable(this.getTableKey('ScrollTable'));
            if (scroll && document.querySelector('.mat-table-container')) {
                document.querySelector('.mat-table-container').scroll(0, scroll);
            }
        }, 0);
    }

    /** Проставление расширенного фильтра */
    setExtendFilter() {
        if (this.form && this.form?.valid) {
            this.extendFilter = this.form.value;
            this.saveScrollTable(true);
            this.getData(false);
            this.token.saveFilterTable(this.getTableKey('FilterTable'), this.extendFilter);
            this.appFormFilter?.isFormData(true);

            if (this.appFormFilter.isSubForm()) {
                this.formFilter.isFilterSet = true;
            }
            this.formFilter.isOpenFilter = false;
        } else if (!this.form) {
            this.formFilter.isFilterSet = false;
            this.saveScrollTable(true);
            this.getData(false);
        }
    }

    /** Очистка расширенного фильтра */
    clearExtendFilter(loadTable = true) {
        this.form.reset();
        this.extendFilter = {};

        this.saveScrollTable(true);

        if (loadTable) {
            this.getData(false);
        }

        this.token.saveFilterTable(this.getTableKey('FilterTable'), this.extendFilter);
        this.appFormFilter.isFormData();

        this.formFilter.isFilterSet = false;
        this.formFilter.isOpenFilter = false;
    }

    /**
     * Очистка полей расширенного фильтра
     */
    resetExtendFilter() {
        this.form.reset();
    }

    /**
     * Изменение пагинации
     * @param event событие пагинации
     */
    onPaginateChange(event?: PageEvent): PageEvent {
        this.settingsTable.userIndex = event.pageIndex;
        this.settingsTable.userSize = event.pageSize;
        this.saveScrollTable(true);
        if (this.tableService) {
            let settingData = Object.assign({}, this.settingsTable);
            if (this.dynamicFilter) {
                settingData = Object.assign(settingData, this.dynamicFilter);
            }
            if (this.tableModule) {
                settingData.module = this.tableModule;
            }
            this.tableService.saveTableSettings(settingData).toPromise();
        }
        this.getData(true);
        return event;
    }

    /**
     * Обработка события сортировки от попапа с настройкой сортировки
     * @param event новый набор колонок для сортировки
     */
    setSortColumns(event: ISortRun[]): void {
        if (event.length > 5) {
            this.notifier.error(this.appService.language_dictionary.t_notification_action_limit);
            return;
        }
        this.settingsTable.userSort = event.map((x) => ({
            name: x.userColumnName,
            sort: x.sortDirection,
        }));
        this.normalizeUserSort();
        this.#afterChangingSettings();
    }

    /**
     * Обработка события сортировки от заголовка колонки таблицы
     * @param column новые параметры сортировки для этой колонки
     */
    modifySortColumn(column: ISortRun) {
        const target = this.settingsTable.userSort.find((x) => x.name === column.userColumnName);
        if (!target) {
            if (this.settingsTable.userSort.length >= 5) {
                this.notifier.error(this.appService.language_dictionary.t_notification_action_limit);
                return;
            }
            this.settingsTable.userSort.push({
                name: column.userColumnName,
                sort: column.sortDirection,
            });
        } else {
            if (column.sortDirection === '') {
                this.settingsTable.userSort = this.settingsTable.userSort.filter((x) => x.name !== target.name);
                if (!this.settingsTable.userSort.length) {
                    this.resetSortColumns();
                    return;
                }
            } else {
                target.sort = column.sortDirection;
            }
        }
        this.#afterChangingSettings();
    }

    /**
     * Применить сортировку по умолчанию
     */
    resetSortColumns() {
        this.notifier.notify('success', this.appService.language_dictionary.t_notification_sort_reset);
        this.settingsTable.userSort = (this.settingsTable.adminSort ?? []).map((x) => ({ name: x.name, sort: x.sort }));
        this.normalizeUserSort();
        this.#afterChangingSettings();
    }

    /**
     * Обработка события Группировки
     * @param event событие группировки
     * @param reset Необходимость сброса группировки
     */
    groupEvent(event: IGroupRun[], reset = false): void {
        if (
            this.getCountProperty('userGroup', false) === 5 &&
            event.length > 0 &&
            !this.settingsTable.userGroup.find((groupField) => groupField.name === event[0].userColumnName)
        ) {
            // Если элементов группировки уже 5 и добавляется новый элемент
            this.notifier.error(this.appService.language_dictionary.t_notification_action_limit);
            return;
        }

        if (reset || event.length === 0 || !this.settingsTable.userGroup) {
            this.settingsTable.userGroup = [];
        }
        // Обрабатываем все элементы
        event.forEach((item: IGroupRun) => {
            let isUpdate = false;
            this.settingsTable.userGroup.forEach((groupColumn) => {
                if (groupColumn.name === item.userColumnName) {
                    groupColumn.sort = item.sortDirection;
                    isUpdate = true;
                    return;
                }
            });
            if (!isUpdate) {
                // Находим тип для каждого поля группировки
                const column = this.settingsTable.userColumns.find((elem) => elem.name === item.userColumnName);
                const fieldFactorType = column?.factor_type;
                const fieldType = column?.type;
                if (fieldFactorType || fieldType) {
                    this.settingsTable.userGroup.push({
                        name: item.userColumnName,
                        sort: item.sortDirection,
                        factor_type: fieldFactorType ?? fieldType,
                    });
                }
            }
        });

        if (this.settingsTable.userGroup.length > 0) {
            this.groupByTreeColumns.isTree = true;
            this.isGroup = true;
        } else {
            this.groupByTreeColumns.isTree = false;
            this.isGroup = false;
        }

        this.#afterChangingSettings();
    }

    /**
     * Обработка события Фильтрации
     * @param event событие фильтрации
     */
    filterEvent(event: IFilterRun[]) {
        this.settingsTable.userFilter = {};
        event.forEach((item: IFilterRun) => {
            if (item.userColumnName !== '') {
                const column: InterfaceSettingsUserColumns = this.settingsTable.userColumns.find(
                    (elem) => elem.name === item.userColumnName,
                );
                const columnType = column.type;
                let value = item.searchValue;

                if (columnType === 'float') {
                    value = value.replace(',', '.');
                }

                if (!this.settingsTable.userFilter[item.userColumnName]) {
                    this.settingsTable.userFilter[item.userColumnName] = [value];
                } else {
                    this.settingsTable.userFilter[item.userColumnName].push(value);
                }
            }
        });
        this.#afterChangingSettings();
    }

    /**
     * Действия после изменения настроек таблицы
     * @property getData - Признак необходимости запроса данных
     */
    #afterChangingSettings(getData: boolean = true) {
        this.#checkExistActionColumn();

        if (this.tableService) {
            let settingData = Object.assign({}, this.settingsTable);
            if (this.dynamicFilter) {
                settingData = Object.assign(settingData, this.dynamicFilter);
            }
            if (this.tableModule) {
                settingData.module = this.tableModule;
            }
            this.tableService.saveTableSettings(settingData).toPromise();
        }

        this.#setStatePaletteColumns(this.settingsTable.palette ?? []);
        this.#checkDisplayPaletteIndicators();

        this.saveScrollTable(true);
        if (getData) {
            setTimeout(() => {
                this.getData(true);
            }, 100);
        }
    }

    /**
     * Соответствует ли количество выбранных элементов общему количеству строк.
     */
    isAllSelected() {
        const selectableRows = this.dataTables.filteredData.filter((row) => this.isRowSelectable(row));
        return this.selection.selected.length === selectableRows.length;
    }

    /**
     * Выбирает все строки, если они не все выделены; в противном случае снимает выбор.
     */
    masterToggle() {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            // Фильтруем элементы, которые можно выделять
            const selectableItems = this.dataTables.filteredData.filter((item) => this.isRowSelectable(item));
            this.selection.select(...selectableItems);
        }
    }

    /** Проверка, можно ли выделить строку */
    isRowSelectable(item: any): boolean {
        return item?.type !== 'group';
    }

    /**
     * Формирует aria-label для чекбокса
     * @param row строка
     */
    checkboxLabel(row?: any): string {
        if (!row) {
            return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
    }

    /**
     * Растягивание колонок таблицы по всей ширине
     */
    setAutoWidth() {
        const tableElement = document.getElementsByClassName(this.classTable);
        let table;
        if (tableElement && tableElement[0]) {
            table = tableElement[0];
        } else {
            table = document;
        }
        const tableContainer = table.querySelectorAll('.mat-table-container-table')[0] as HTMLDivElement;
        const tableDiv = table.querySelectorAll('.mat-table-container-table .mat-table')[0] as HTMLDivElement;
        if (tableContainer) {
            let width = tableContainer.offsetWidth - tableDiv.offsetWidth;
            if (tableContainer.scrollHeight !== tableContainer.offsetHeight) {
                width -= 18;
            }
            if (width < 1) {
                return;
            }
            let fixedColumnsCount = 0;
            this.settingsTable.userColumns.forEach((column) => {
                if (!column.is_fixed) {
                    fixedColumnsCount++;
                }
            });
            if (fixedColumnsCount > 0) {
                const widthAdd = width / fixedColumnsCount;

                const nameColumns = [];
                this.settingsTable.userColumns.forEach((column) => {
                    if (!column.is_fixed) {
                        column.width = parseFloat(String(column.width)) + widthAdd;
                        nameColumns.push(column.name);
                    }
                });
                this.settingsTable.userTree.select.forEach((column) => {
                    if (nameColumns.indexOf(this.getAliasColumn(column)) !== -1) {
                        column.width = parseFloat(column.width + '') + widthAdd;
                    }
                });
                this.dataMatTable.updateStickyColumnStyles();
            }
            this.#cdr.detectChanges();
        }
    }

    /**
     * Открытие диалога настроек таблицы
     */
    openSettingsDialog() {
        const dialogRef = this.dialog.open(SettingsTableDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '80vw',
            height: '80vh',

            data: {
                title: this.appService.language_dictionary.system_settings_table_title,
                settings: this.settingsTable.userTree,
                tableService: this.tableService,
                dynamicFieldName: this.dynamicFieldName,
                dynamicSystemFields: this.dynamicSystemFields,
                dynamicFilter: this.dynamicFilter,
            },
        });
        dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    this.spinner.hide();
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.save) {
                    let isMain = false;
                    const userColumns = [];

                    this.settingsTable.displayedColumns = ['select'];
                    if (this.dataPalette && this.dataPalette.length) {
                        this.dataPalette.forEach((palette, index) => {
                            if (this.settingsTable.displayedColumns[index] !== 'system_color_' + palette.index) {
                                this.settingsTable.displayedColumns.splice(index, 0, 'system_color_' + palette.index);
                            }
                        });
                    }
                    this.clearDisplayedColumns();
                    this.settingsTable.userTree = result.settings;
                    setTimeout(() => {
                        this.settingsTable.userTree.select.forEach((node) => {
                            const name = this.getAliasColumn(node);
                            if (node.is_main) {
                                isMain = true;
                            }
                            userColumns.push({
                                name,
                                title: node.name,
                                factor_type: node.factor_type,
                                type: node.type,
                                is_main: node.is_main,
                                is_sticky: node.is_sticky,
                                width: !!node.width ? node.width : 100,
                                is_fixed: !!node.is_fixed,
                                is_group: node.columns.length !== 0,
                                transformParam: node?.transformParam,
                                columns: node.columns,
                            });
                            if (this.dynamicFilter) {
                                userColumns[userColumns.length - 1].id = node.id;
                                userColumns[userColumns.length - 1].name = name;
                                if (node.relation_id) {
                                    userColumns[userColumns.length - 1].relation_id = node.relation_id;
                                }
                            }
                            this.settingsTable.displayedColumns.push(name);
                        });
                        this.settingsTable.displayedColumns = this.#sortDisplayedColumns(
                            this.settingsTable.displayedColumns,
                        );
                        this.#checkDisplayPaletteIndicators();

                        if (!isMain) {
                            userColumns[0].is_main = true;
                        }
                        // Если в фильтре остались колонки, которых больше нет, необходимо от них избавиться
                        Object.keys(this.settingsTable?.userFilter || {}).forEach((key) => {
                            const transformKey = key.replace('|', '_');
                            if (userColumns.findIndex((row) => row?.name === transformKey) === -1) {
                                delete this.settingsTable.userFilter[key];
                            }
                        });

                        this.settingsTable.userColumns = userColumns;
                        this.normalizeUserSort();
                        this.#checkExistActionColumn();

                        if (this.tableService) {
                            let settingData = Object.assign({}, this.settingsTable);
                            if (this.dynamicFilter) {
                                settingData = Object.assign(settingData, this.dynamicFilter);
                            }
                            if (this.tableModule) {
                                settingData.module = this.tableModule;
                            }

                            this.tableService
                                .saveTableSettings(settingData)
                                .toPromise()
                                .then(() => {
                                    this.getData(true);
                                });

                            this.#setStatePaletteColumns(this.settingsTable.palette ?? []);

                            setTimeout(() => {
                                this.setAutoWidth();
                            }, 100);
                        }
                    });
                } else if (result.saveCard) {
                    this.saveCard.emit({
                        isClearTableSettings: result.isClearTableSettings,
                        dynamicFilter: this.dynamicFilter,
                        tableService: this.tableService,
                    });
                }
            });
    }

    /**
     * Проставление функций для отрисовки
     */
    setCellForColumns() {
        this.settingsTable.userColumns.forEach((sort) => {
            this.settingsTable.userTree.select.forEach((node) => {
                if (
                    !sort.is_group &&
                    this.dynamicFilter &&
                    node.id == sort.id &&
                    node.relation_id == sort.relation_id
                ) {
                    node.alias = sort.name;
                }
            });
        });
    }

    /**
     * Получение алиаса колонки
     * @param node: {TreeNode} node ветка дерева колонок
     */
    getAliasColumn(node: TreeNode): string {
        let name = '';
        if (node.columns.length == 0) {
            name = node.alias;
        } else {
            const str = [];
            node.columns.forEach((alias) => str.push(alias.alias));
            name = str.join('_');
        }
        return name;
    }

    /**
     * Построение подсказки к ячейке
     */
    getCellTooltip(column: any, row: any): string {
        const alias = !column.alias ? column.name : column.alias;
        if (row[alias] === undefined || row[alias] === null) {
            return '';
        }

        if (!column.columns || !column.columns.length || !Array.isArray(column.columns)) {
            const str = row[alias] + '';
            if (column.type === 'text') {
                return str.replace(/<\s*(div|p|h[1-6]|ul|ol|li|blockquote|br)(\s+[^>]*?)?\s*>/g, ' ');
            }

            return str.replace(/{br}/g, ', ');
        }

        return (column.columns as TreeNode[]).map((x) => this.getCellTooltip(x, row)).join(', ');
    }

    /**
     * Создание нового фильтра
     */
    createFilter(data) {
        if (!data) {
            return;
        }

        if (this.dynamicFilter) {
            data = Object.assign(data, this.dynamicFilter);
        }
        this.spinner.show(this.spinnerName);

        this.filterService
            .createFilter(data)
            .toPromise()
            .then((result: IAppRequest) => {
                this.spinner.hide(this.spinnerName);

                if (result && result.is_error == false) {
                    if (this.isLocalFilter) {
                        this.extendFilterRecordsModel.push(result.data);
                        this.extendFilterIdModel = result.data.id;
                        this.setActiveFilter(this.extendFilterIdModel);
                    } else {
                        this.formFilter.savedSerchesService.filterRecords$.pipe(take(1)).subscribe((list) => {
                            list.push(result.data);
                            this.formFilter.savedSerchesService.filterRecords = list;
                        });

                        this.formFilter.savedSerchesService.selectedFilterId = result.data.id;
                    }
                    this.notifier.notify('success', this.appService.language_dictionary.system_data_successfully_saved);
                } else {
                    this.notifier.notify('error', result.message);
                }
            })
            .catch(() => {
                this.spinner.hide(this.spinnerName);
                this.notifier.notify('error', this.appService.language_dictionary.system_no_connection_with_system);
            });
    }

    /**
     * Сохранение фильтра
     */
    saveFilter() {
        if (this.selectFilter) {
            this.router.navigate(['designer-filter', this.selectFilter.id ? this.selectFilter.id : 0]);
        }
    }

    /**
     * Выбор активного фильтра
     * @param id ид фильтра
     */
    setActiveFilter(id: string) {
        /* Переводим пользователя на первую страницу таблицы */
        this.settingsTable.userIndex = 1;
        const localFilter = this.token.getFilterTable(this.getTableKey('FilterTable'));
        this.form = this.formBuilder.group({});
        if (this.isLocalFilter) {
            this.extendFilterRecordsModel.forEach((record) => {
                if (record.id == id) {
                    this.selectFilter = Object.assign({}, record);
                }
            });
        } else {
            this.formFilter.savedSerchesService.filterRecords$.pipe(take(1)).subscribe((list) => {
                list.forEach((record) => {
                    if (record.id == id) {
                        this.selectFilter = Object.assign({}, record);
                    }
                });
            });
        }
        if (this.selectFilter && (this.settingsTable.filterId ?? -1) != this.selectFilter.id) {
            this.settingsTable.filterId = this.selectFilter.id;
            if (this.tableService) {
                let settingData = Object.assign({}, this.settingsTable);
                if (this.dynamicFilter) {
                    settingData = Object.assign(settingData, this.dynamicFilter);
                }
                if (this.tableModule) {
                    settingData.module = this.tableModule;
                }
                this.tableService.saveTableSettings(settingData).toPromise();
            }
        }
        if (this.isFirstLoadFilter && this.selectFilter && localFilter) {
            this.selectFilter.query = localFilter;
            this.extendFilter = localFilter;
            this.isFirstLoadFilter = false;
            this.getData(true);

            const showFilter = this.isFilterSet();
            this.formFilter.isFilterSet = showFilter;
            this.formFilter.isOpenFilter = showFilter;
        } else if (this.selectFilter && (this.selectFilter.query || this.settingsTable.filterId)) {
            this.extendFilter = this.selectFilter.query;
            this.getData(true);
            this.token.saveFilterTable(this.getTableKey('FilterTable'), this.extendFilter);
            this.formFilter.isOpenFilter = false;
            this.formFilter.isFilterSet = this.isFilterSet();
        }
    }

    /**
     * Открытие диалога сохранения фильтра
     */
    openFilterDialog(): void {
        const dialogRef = this.dialog.open(FilterDialogsComponent, {
            disableClose: true,
            autoFocus: false,
            width: '600px',
            data: {
                title: this.appService.language_dictionary.system_add,
            },
        });
        dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.save) {
                    const data = {
                        id: this.selectFilter.id,
                        name: result.title,
                        query: this.form.value,
                    };
                    this.createFilter(data);
                }
            });
    }

    /**
     * Проверка пустоты фильтров
     */
    isEmptyFilter(): boolean {
        if (this.globalFilter) {
            return false;
        }

        if (!this.isEmptyData(this.extendFilter)) {
            return false;
        }
        if (this.settingsTable.userFilter && !this.isEmptyData(this.settingsTable.userFilter)) {
            return false;
        }

        return true;
    }

    /**
     * Проверка наличия данных в объекте фильтра
     * @param data данные фильтра
     */
    isEmptyData(data: any): boolean {
        let isEmpty = true;

        for (const val of Object.values(data)) {
            switch (typeof val) {
                case 'object':
                    if (val === null) {
                        break;
                    }
                    isEmpty = this.isEmptyData(val);
                    break;
                case 'boolean':
                case 'number':
                    isEmpty = false;
                    break;
                default:
                    isEmpty = !Boolean(val);
            }
            if (!isEmpty) {
                return isEmpty;
            }
        }
        return isEmpty;
    }

    /**
     * Добавление слоя
     */
    openAddLayer(): void {
        const dialogRef = this.dialog.open(TableLayerDialogComponent, {
            disableClose: true,
            autoFocus: false,
            width: '800px',
            data: {
                tableService: this.tableService,
                query: this.extendFilter,
                dynamicFilter: this.dynamicFilter,
                title: this.appService.language_dictionary.system_layer_dialog_title_add,
                id: this.isLocalFilter
                    ? this.extendFilterIdModel
                    : this.formFilter.savedSerchesService.selectedFilterId,
            },
        });
        dialogRef
            .keydownEvents()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.code === 'Escape') {
                    dialogRef.close({ save: false });
                }
            });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result.save) {
                    this.notifier.notify('success', this.appService.language_dictionary.system_data_successfully_saved);
                }
            });
    }

    /** Формирование данных для дерева */
    public groupByTree(): void {
        this.dataTables.filteredData = this.addGroupsTree(this.dataTables.filteredData);
        this.filteredDataTreeAll = this.dataTables.filteredData.slice();

        // Применяем состояние развернутости, что сохранено у пользователя
        if (this.settingsTable.userGroupExpanded !== undefined) {
            this.toggleExpandAll(this.settingsTable.userGroupExpanded);
        }
    }

    /**
     * Генерация данных для дерева
     * @param data данные дерева
     * @param parent_id ид родительской записи
     * @param level уровень ветки в дереве
     * @return TreeNodeRegister[]
     */
    generateTreeData(data: any[], parent_id: string | number, level: number): any[] {
        const result: any[] = [];
        data.forEach((value) => {
            if (value.parent_id == parent_id) {
                const node: any = Object.assign({}, value);
                node.level = level;
                node.expanded = true;
                node.children = this.generateTreeData(data, node.id, level + 1);
                node.is_children = node.children.length != 0;
                result.push(node);
            }
        });
        return result;
    }

    /**
     * Генерация массива для дерева
     * @param data данные таблицы
     * @param parent родительская запись
     */
    generateArrayTree(data: any[], parent: any) {
        let result: any[] = [];
        data.forEach((value) => {
            const node = Object.assign({}, value);
            delete node.children;
            node.parent = parent;
            result.push(node);
            if (value.is_children) {
                result = result.concat(this.generateArrayTree(value.children, node));
            }
        });
        return result;
    }

    /**
     * Нажатие на строку
     * @param row строка
     */
    groupHeaderTreeClick(event: MouseEvent, row: any) {
        row.expanded = !row.expanded;
        const data = [];
        this.filteredDataTreeAll.forEach((value) => {
            if (!value?.parent || (value?.parent && this.isExpandedParents(value?.parent))) {
                data.push(value);
            }
        });
        this.dataTables.filteredData = data;
        event.preventDefault();
        event.stopImmediatePropagation();
    }

    /**
     * Свернуть/развернуть все элементы таблицы
     * @param expand - разворачиваем или сворачиваем
     */
    toggleExpandAll(expand: boolean) {
        // Проходим по всем элементам дерева
        this.filteredDataTreeAll.forEach((item) => {
            item.expanded = expand; // Устанавливаем состояние для всех элементов
        });

        // Обновляем отображаемые данные
        const data = [];
        this.filteredDataTreeAll.forEach((value) => {
            if (!value?.parent || (value?.parent && this.isExpandedParents(value?.parent))) {
                data.push(value);
            }
        });
        this.dataTables.filteredData = data;
    }

    /**
     * Проверка отображения строк
     * @param parent родительская запись
     */
    isExpandedParents(parent: any) {
        let expanded = parent.expanded;
        if (expanded && parent.parent) {
            expanded = this.isExpandedParents(parent.parent);
        }
        return expanded;
    }

    /**
     * Формирование данных для таблицы древовидной формы
     * @param data
     */
    addGroupsTree(data: any[]): any[] {
        const tree = this.generateTreeData(data, this.groupByTreeColumns.rootId, 1);
        return this.generateArrayTree(tree, null);
    }

    /**
     * Инициализация формы редактирование строк в таблице
     */
    initFormEdit() {
        this.formEdit = this.formBuilder.group({});
        this.dataTables.filteredData.forEach((row: any) => {
            Object.keys(this.settingsEditFields.fields).forEach((field) => {
                const formGroup = this.formBuilder.group({});
                this.formEdit.addControl(field + '_' + row?.id, formGroup);
                formGroup.addControl('value', this.formBuilder.control(row[field] ? row[field] : null, []));
                formGroup.addControl('id', this.formBuilder.control(row?.id ? row.id : null, []));
                formGroup.addControl('field', this.formBuilder.control(field, []));
            });
        });
        for (const control in this.formEdit.controls) {
            if (this.formEdit.controls.hasOwnProperty(control)) {
                this.formEdit
                    .get(control)
                    .valueChanges.pipe(debounceTime(this.appService.timeSave), takeUntil(this.destroy$))
                    .subscribe((value) => this.onFieldChange.next(value));
            }
        }
    }

    /**
     * Удаление списка записей
     * @param selected выбранные записи
     */
    removeRecords(selected: any[]) {
        const ids = [];
        selected.forEach((value) => ids.push(value.id));
        this.dialogConfirm
            .confirm(
                this.appService.language_dictionary.system_confirm_action,
                this.appService.language_dictionary.system_are_you_sure_want_delete,
            )
            .pipe(take(1))
            .subscribe((res) => {
                if (res) {
                    this.spinner.show(this.spinnerName);
                    this.tableService
                        .remove({ id: ids })
                        .toPromise()
                        .then((result: IAppRequest) => {
                            this.spinner.hide(this.spinnerName);
                            if (result && result.is_error === false) {
                                this.notifier.notify(
                                    'success',
                                    this.appService.language_dictionary.system_data_successfully_deleted,
                                );
                                this.getData(true);
                            } else {
                                this.notifier.notify('error', result.message);
                            }
                        })
                        .catch(() => {
                            this.spinner.hide(this.spinnerName);
                            this.notifier.notify(
                                'error',
                                this.appService.language_dictionary.system_no_connection_with_system,
                            );
                        });
                }
            });
    }

    /**
     * Получение сортировки для отправки в запросе
     * @param userSort текущая сортировка
     */
    private getSortParam(userSort: { name: string; sort: TCustomSortDirection }[]) {
        const sortResult = [];
        if (userSort) {
            userSort.forEach((sort) => {
                this.settingsTable.userTree.select.forEach((node) => {
                    if (sort.name === this.getAliasColumn(node)) {
                        if (node.columns.length === 0) {
                            sortResult.push(sort);
                        } else {
                            // Разбиение объединенной колонки на составляющие ее колонки
                            node.columns.forEach((column) =>
                                sortResult.push({
                                    name: column.alias,
                                    sort: sort.sort,
                                    type: column.type,
                                    factor_type: column.factor_type,
                                    transformParam: column?.transformParam,
                                }),
                            );
                        }
                    }
                });
            });
        }
        return sortResult;
    }

    /**
     * Получение основной палитры
     */
    getMainPalette(palette: any) {
        for (const key in palette) {
            if (palette[key].main) {
                this.mainPalette = 'color_' + palette[key].index;
            }
        }
    }

    /**
     * Отслеживаем события нажатия на Enter
     */
    @HostListener('document:keypress', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.code === 'Enter' && this.formFilter.isOpenFilter) {
            this.formFilter.isOpenFilter = false;
            this.setExtendFilter();
        }
    }
    @HostListener('document:keydown', ['$event'])
    handleKeyboardEventTwo(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.#turnFullScreenOff();
        }
    }

    /** Плавное отображение хедера таблицы при наведении на панель инструментов таблицы */
    @HostListener('mouseover', ['$event'])
    onMouseEnter(event: MouseEvent) {
        if (this.isFullScreenMode && this.tablePanel?.nativeElement?.contains(event.target as Node)) {
            this.tableHeader?.nativeElement.classList.add('show');
        }
        if (this.matTable && this.matTable?.nativeElement?.contains(event.target as Node)) {
            this.tableHeader?.nativeElement.classList.remove('show');
        }
    }

    /**
     * Получить выравнивание в ячейке
     * @param column - колонка
     */
    getAlign(column): string | boolean {
        if (column && column.type) {
            switch (column.type) {
                case 'float':
                case 'int':
                case 'integer':
                    return 'right';
                default:
                    return false;
            }
        }
        return false;
    }

    /**
     * Добавить сквозную нумерацию в таблицу
     */
    getTablePagination(): number {
        if (this.settingsTable.userIndex !== 1) {
            return this.settingsTable.userSize * (this.settingsTable.userIndex - 1);
        }
        return 0;
    }

    /** Получить информацию о столбце группировки для правильного отображения данных */
    getColumnGroupInfo(row: any): InterfaceSettingsUserColumns | null {
        return (
            this.settingsTable.userColumns.find(
                (item: InterfaceSettingsUserColumns) => row.group_column_name === item.name,
            ) ?? null
        );
    }

    // Получаем записи без учета родительских узлов (группировка)
    filteredRows(): any {
        return this.dataTables.filteredData.filter(
            (row: any) => (row.type !== 'group' && this.isGroup) || !this.isGroup,
        );
    }

    /**
     * Проверка того, нужно ли задизаблить кнопку
     * @param button кнопка, для которой проводится проверка
     */
    isDisabledButton(button: IButton): boolean {
        if (button.records === 'one' && this.selection.selected.length !== 1) {
            return true;
        }

        if (button.records === 'many' && this.selection.selected.length === 0) {
            return true;
        }

        if (typeof button.disabled === 'boolean') {
            return button.disabled;
        }

        if (typeof button.disabled === 'function') {
            return button.disabled(this.selection.selected);
        }

        if (typeof button.disabled === 'object') {
            return (
                button.records === 'one' &&
                this.selection.selected.length === 1 &&
                this.selection.selected[0][button?.disabled?.field] === button?.disabled?.condition
            );
        }

        return false;
    }

    /**
     * Увеличиваем счетчик построенных элементов
     * @module report_constructor
     */
    increaseCount(): void {
        if (this.tableService?.exportReady$) {
            // В момент построения отчета изменяем количество готовых элементов
            const num = this.tableService.exportReady$.value + 1;
            this.tableService.exportReady$.next(num);
        }
    }

    /**
     * Обнаружить изменения
     */
    public detectChanges() {
        this.#cdr.detectChanges();
    }

    /**
     * Определяет порядок displayedColumns в таблице.
     * Порядок - fixedOrder, systemColorColumns, все остальное
     * @param displayedColumns
     */
    #sortDisplayedColumns(displayedColumns: string[]): string[] {
        // Столбцы которые всегда есть
        const fixedOrder = TABLE_FIXED_COLUMS;
        // Столбцы палитры
        const systemColorColumns = displayedColumns.filter((col) => col.startsWith('system_color_'));

        const columns = [...fixedOrder, ...systemColorColumns];

        // Все остальные столбцы
        const unknownColumns = displayedColumns.filter((col) => !columns.includes(col));

        return [...columns, ...unknownColumns];
    }

    /**
     * Обработчик для выделения по нажатию shift
     */
    selectionKeyboardHandler() {
        let isSelectingRange = false;
        let rangeToSelect = [];

        this.selection.changed.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (isSelectingRange) {
                return;
            }

            if (this.shiftKeyPressed$.getValue()) {
                const selectedItems = this.selection.selected;
                if (selectedItems.length > 0) {
                    const firstSelectedItem = selectedItems[0];
                    const lastSelectedItem = selectedItems[selectedItems.length - 1];

                    const filteredData = this.dataTables.filteredData;
                    let firstIndex = filteredData.findIndex((item) => item === firstSelectedItem);
                    let lastIndex = filteredData.findIndex((item) => item === lastSelectedItem);

                    if (firstIndex > lastIndex) {
                        const temp = firstIndex;
                        firstIndex = lastIndex;
                        lastIndex = temp;
                    }

                    if (firstIndex !== -1 && lastIndex !== -1) {
                        rangeToSelect = filteredData.slice(firstIndex, lastIndex + 1);
                    }
                }

                if (rangeToSelect?.length) {
                    isSelectingRange = true;
                    rangeToSelect.forEach((item) => this.selection.select(item));
                    isSelectingRange = false;
                }
            }
        });
    }

    @HostListener('window:keydown.shift', ['$event'])
    handleShiftDown(_event) {
        if (!this.shiftKeyPressed$.getValue()) {
            this.shiftKeyPressed$.next(true);
        }
    }

    @HostListener('window:keyup.shift', ['$event'])
    handleShiftUp(_event) {
        if (this.shiftKeyPressed$.getValue()) {
            this.shiftKeyPressed$.next(false);
        }
    }

    /**
     * Проверка есть ли что-то выбраное в расширенном фильтре
     * (local storage)
     */
    isFilterSet(): boolean {
        const localFilter = this.token.getFilterTable(this.getTableKey('FilterTable'));

        const hasNonNullValue = (obj: any): boolean => {
            if (obj === null || obj === undefined) {
                return false;
            }

            if (typeof obj === 'object') {
                return Object.values(obj).some((innerVal) => {
                    if (innerVal === null || innerVal === undefined) {
                        return false;
                    }
                    return hasNonNullValue(innerVal);
                });
            }

            return true;
        };

        return Object.values(localFilter).some((val) => hasNonNullValue(val));
    }

    /**
     * Открыть/закрыть фильтр
     */
    toggleFilter() {
        this.formFilter.isOpenFilter = !this.formFilter.isOpenFilter;
    }

    /**
     * Клик по строке таблицы
     */
    handleRowClick(event: MouseEvent, row: any) {
        if (this.settingsEditFields?.isEdit) {
            return;
        }

        if (this.isGroup && row?.type === 'group') {
            this.groupHeaderTreeClick(event, row); // Если это родительская строка
        } else {
            this.selection.toggle(row); // Обычная обработка клика
        }
    }

    /**
     * Двойной клик по строке таблицы
     */
    handleDblClick(event: MouseEvent, row: any) {
        if (row.type !== 'group') {
            event.stopPropagation();
            this.callParent('dblClickRow', row, event);
        }
    }

    /**
     * Правый клик по строке (отображение меню)
     */
    onRightClick(event: MouseEvent, row: any): void {
        event.preventDefault();
        if (row?.type !== 'group' || !row.type) {
            this.selection.select(row); // Группы не выделяем
        }
        this.tableHeaderComponent?.openContextMenu(event);
    }

    /**
     * Выход из полноэкранного режима
     */
    #turnFullScreenOff() {
        this.isFullScreenMode = false;
        this.appService.showSideMenu.next(true);
        this.adjustButtons();
        if (this.tableHeader) {
            this.tableHeader.nativeElement.classList.remove('show');
        }
    }

    /**
     * Открытие попапа настроек сортировки
     */
    openSortPopup() {
        this.columnActionType = EActionColumn.sort;
        this.columnActionTooltip.next(null);

        this.valuesFormColumnActions = (this.settingsTable.userSort ?? []).map(
            (item) =>
                ({
                    userColumnName: item.name,
                    sortOrderOrSearchValue: item.sort,
                }) as IValueForm,
        );

        const rect = this.sortButtonRef.nativeElement.getBoundingClientRect();
        this.tableColumnAction.show(rect.left, rect.bottom);
    }

    openGroupPopup(groupData: IGroupRun = null) {
        this.columnActionType = EActionColumn.group;
        this.columnActionTooltip.next(null);

        const userGroup: IValueForm[] =
            this.settingsTable?.userGroup?.map((item) => ({
                userColumnName: item.name,
                sortOrderOrSearchValue: item.sort,
            })) || [];

        if (groupData) {
            userGroup.push({
                userColumnName: groupData.userColumnName,
                sortOrderOrSearchValue: groupData.sortDirection,
            });
            this.#afterChangingSettings();
        }

        this.valuesFormColumnActions = Object.values(userGroup);

        const rect = this.groupButtonRef.nativeElement.getBoundingClientRect();
        this.tableColumnAction.show(rect.left, rect.bottom);
    }

    openFilterPopup(dataFilter: IFilterRun = null) {
        this.columnActionType = EActionColumn.filter;
        this.columnActionTooltip.next(null);

        const userFilter: IValueForm[] = this.settingsTable?.userFilter
            ? Object.entries(this.settingsTable.userFilter).flatMap(([name, values]) =>
                  values.map((value) => ({
                      userColumnName: name,
                      sortOrderOrSearchValue: value,
                  })),
              )
            : [];

        if (dataFilter) {
            userFilter.push({
                userColumnName: dataFilter.userColumnName,
                sortOrderOrSearchValue: dataFilter.searchValue,
            });
        }

        this.valuesFormColumnActions = Object.values(userFilter);

        let left = dataFilter?.left;
        let bottom = dataFilter?.bottom;
        if (!left) {
            const rect = this.filterButtonRef.nativeElement.getBoundingClientRect();
            left = rect.left;
            bottom = rect.bottom;
        }

        this.tableColumnAction.show(left, bottom);
    }

    handlePinToggle(pinToggleData: IPinOrUnpinRun) {
        this.settingsTable.userTree.select.forEach((item) => {
            if (item.alias === pinToggleData.userColumnName) {
                item.is_sticky = !item.is_sticky;
            }
        });

        this.settingsTable.userColumns.forEach((item) => {
            if (item.name === pinToggleData.userColumnName) {
                item.is_sticky = !item.is_sticky;
            }
        });

        this.#afterChangingSettings(false);
    }

    /** Скрыть отображаемый столбец таблицы пользователя */
    handleHideColumn(hideData: IHideRun) {
        const groupIdx = (this.settingsTable.userGroup ?? []).findIndex((x) => x.name === hideData.userColumnName);
        if (groupIdx !== -1) {
            this.settingsTable.userGroup.splice(groupIdx, 1);
        }

        this.settingsTable.userColumns = this.settingsTable.userColumns.filter(
            (item) => item.name !== hideData.userColumnName,
        );

        this.settingsTable.userTree.select = this.settingsTable.userTree.select.filter(
            (item) => item.alias !== hideData.userColumnName,
        );

        // Добавляем столбец в дерево не добавленных колонок
        this.settingsTable.allColumns.forEach((registry) => {
            const findColumn: InterfaceSettingsUserColumns = registry.columns.find(
                (column) => column.name === hideData.userColumnName,
            );
            if (findColumn) {
                const treeColumn = {
                    id: findColumn.id as string,
                    name: findColumn.title,
                    alias: findColumn.name,
                    factor_type: findColumn.factor_type,
                    parent: registry.entity,
                    type: findColumn.type,
                    is_fixed: false,
                    is_main: false,
                    is_sticky: false,
                    columns: [],
                    level: 2,
                };
                this.settingsTable.userTree.all.forEach((registry2) => {
                    if (registry2.name === registry.entity) {
                        registry2.children = [...registry2.children, treeColumn];
                    }
                });
            }
        });

        this.settingsTable.displayedColumns = this.settingsTable.displayedColumns.filter(
            (item) => item !== hideData.userColumnName,
        );

        this.#afterChangingSettings(groupIdx !== -1);
    }

    groupCustomAction(actionName: TGroupCustomAction) {
        switch (actionName) {
            case 'collapse':
                this.settingsTable.userGroupExpanded = false;
                this.toggleExpandAll(false);
                break;
            case 'expand':
                this.settingsTable.userGroupExpanded = true;
                this.toggleExpandAll(true);
                break;
        }
        // Сохраняем настройки таблицы
        this.#afterChangingSettings(false);
    }

    /** Получить длину свойства таблицы */
    getCountProperty(propertyName: string, isString = true): string | number {
        if (propertyName === 'userFilter') {
            // По одной колонке можно фильтровать несколько значений, считаем кол-во значений
            const propertyValues = this.settingsTable[propertyName];
            if (propertyValues === undefined || propertyValues === null) {
                return isString ? '' : 0;
            }

            // Суммируем длины всех массивов в объекте
            const totalCount = Object.values(propertyValues).reduce((count, array) => {
                return count + (Array.isArray(array) ? array.length : 0);
            }, 0);

            return totalCount > 0 ? (isString ? ` (${totalCount})` : totalCount) : isString ? '' : 0;
        }
        const propertyValue = this.settingsTable[propertyName];
        if (propertyValue === undefined || propertyValue === null) {
            return isString ? '' : 0;
        }
        return isString ? ' (' + Object.keys(propertyValue).length + ')' : Object.keys(propertyValue).length;
    }

    /** Признак работы с действием */
    isWorkingAction(propertyName: string): boolean {
        const propertyValue = this.settingsTable[propertyName];
        if (propertyValue === undefined || propertyValue === null) {
            return false;
        }
        return Object.keys(this.settingsTable[propertyName]).length > 0;
    }

    /**
     * Если мы убрали поле (через настройки таблицы),
     * по которому выполняется сортировка по умолчанию,
     * то переключать сортировку на первое по списку поле.
     * #67465
     */
    normalizeUserSort() {
        if (!this.settingsTable.userColumns?.length) {
            return;
        }
        const filtered = (this.settingsTable.userSort ?? []).filter(
            (userSortColumn) => this.settingsTable.userColumns.findIndex((x) => x.name === userSortColumn.name) !== -1,
        );
        if (filtered.length) {
            return;
        }
        const firstUserColumn = this.settingsTable.userColumns[0];
        this.settingsTable.userSort = [
            {
                name: firstUserColumn.name,
                sort: 'asc',
                type: firstUserColumn.type,
                factor_type: firstUserColumn.factor_type,
            },
        ];
    }

    /** Получить данные о колонке для меню действий над колонкой */
    getDataColumnByMenu(column: InterfaceSettingsUserColumns): IDataColumnMenu {
        const columnSort = (this.settingsTable?.userSort ?? []).find((item) => item.name === column.name);
        const columnGroup = (this.settingsTable?.userGroup ?? []).find((item) => item.name === column.name);
        return {
            title: column.title,
            name: column.name,
            is_sticky: column.is_sticky,
            groupDirection: columnGroup?.sort ?? '',
            sortDirection: columnSort?.sort ?? '',
            factor_type: column?.factor_type ?? 'string',
        };
    }

    /** Колонки палитры - установка состояний столбцов палитры, по которым можно выполнять действия */
    #setStatePaletteColumns(paletteData: IPaletteRecord[]) {
        const columnsDisplayMenu: Record<string, IDataColumnMenu> = {};
        paletteData.forEach((palette) => {
            let columnName = '';
            switch (palette.alias_prompt) {
                case 'factor_statuses':
                    columnName = 'card_status_name';
                    break;
                case 'is_map':
                    columnName = 'has_geo';
                    break;
                default:
                    return;
            }
            const userColumn: InterfaceSettingsUserColumns = this.settingsTable.userColumns?.find(
                (item) => item.name === columnName,
            );

            const userSort = this.settingsTable?.userSort?.find((item) => item.name === columnName);
            const userGroup = this.settingsTable?.userGroup?.find((item) => item.name === columnName);
            columnsDisplayMenu[palette.title] = {
                title: palette.title,
                name: userColumn?.name ?? '',
                is_sticky: userColumn?.is_sticky ?? false,
                groupDirection: userGroup?.sort ?? '',
                sortDirection: userSort?.sort ?? '',
                factor_type: userColumn?.factor_type ?? 'string',
            };
        });
        this.showMenuColumnPalette$.next(columnsDisplayMenu);
    }

    /** Проверяет должны ли выводиться индикаторы палитры в таблицу, убирает их если не должны */
    #checkDisplayPaletteIndicators() {
        // Если колонки "Наличие геопривязки" нет - не отображаем индикатор геопривязки
        if (!this.settingsTable.displayedColumns.includes('has_geo')) {
            this.settingsTable.displayedColumns = this.settingsTable.displayedColumns.filter(
                (item) => item !== 'system_color_1',
            );
        } else if (!this.settingsTable.displayedColumns.includes('system_color_1')) {
            this.settingsTable.displayedColumns.push('system_color_1');
        }

        // Если колонки "Статус записи" нет - не отображаем индикатор
        if (!this.settingsTable.displayedColumns.includes('card_status_name')) {
            this.settingsTable.displayedColumns = this.settingsTable.displayedColumns.filter(
                (item) => item !== 'system_color_2',
            );
        } else if (!this.settingsTable.displayedColumns.includes('system_color_2')) {
            this.settingsTable.displayedColumns.push('system_color_2');
        }
    }

    /** Получить колонки, которые должны отображаться в таблице */
    getDisplayedUserColumn() {
        // Столбец наличия геопривязки не выводим, для него есть индикатор
        // Столбец статуса записи не выводим, для него есть индикатор
        return this.settingsTable.userColumns.filter(
            (item) => item.name !== 'has_geo' && item.name !== 'card_status_name',
        );
    }

    /** Получить колонки, которые должны отображаться в таблице */
    getDisplayedColumn() {
        // Столбец наличия геопривязки не выводим, для него есть индикатор
        // Столбец статуса записи не выводим, для него есть индикатор
        return this.settingsTable.displayedColumns.filter((item) => item !== 'has_geo' && item !== 'card_status_name');
    }

    /**
     * Проверка существования колонок, по которым выполняются действия
     * (удаляет колонку из действия если она не существует)
     */
    #checkExistActionColumn() {
        // Создаем копию объекта, чтобы не мутировать оригинал
        const cleanedSettings = { ...this.settingsTable };

        // Собираем все имена столбцов из userColumns в Set для быстрого поиска
        const validColumnNames = new Set<string>();

        if (cleanedSettings.userColumns) {
            cleanedSettings.userColumns.forEach((column) => {
                validColumnNames.add(column.name);
            });
        }

        // Проверка userSort
        if (cleanedSettings.userSort) {
            cleanedSettings.userSort = cleanedSettings.userSort.filter((sortItem) =>
                validColumnNames.has(sortItem.name),
            );
        }

        // Проверка userGroup
        if (cleanedSettings.userGroup) {
            cleanedSettings.userGroup = cleanedSettings.userGroup.filter((groupItem) =>
                validColumnNames.has(groupItem.name),
            );

            if (cleanedSettings.userGroup.length === 0) {
                this.groupByTreeColumns.isTree = false;
                this.isGroup = false;
            }
        }

        // Проверка userFilter
        if (cleanedSettings.userFilter && Object.keys(cleanedSettings.userFilter).length > 0) {
            const filteredUserFilter: Record<string, string[]> = {};

            Object.keys(cleanedSettings.userFilter).forEach((key) => {
                if (validColumnNames.has(key)) {
                    filteredUserFilter[key] = cleanedSettings.userFilter[key];
                }
            });

            cleanedSettings.userFilter = filteredUserFilter;
        }

        this.settingsTable = cleanedSettings;
    }
}
